/**
 * Post-login target path redirect utility.
 * - Stores target path in localStorage with a 30-minute expiry.
 * - Internal paths only (starts with "/", not "//").
 * - Works across OTP and standard authentication flows.
 */

const REDIRECT_TARGET_KEY = "soorgaa_redirect_target";
const EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

interface RedirectEntry {
  path: string;
  expiresAt: number;
}

export function isValidRedirectPath(path: string): boolean {
  return typeof path === "string" && path.startsWith("/") && !path.startsWith("//");
}

export function saveRedirectTarget(path: string): void {
  if (!isValidRedirectPath(path)) {
    return;
  }
  // Ignore auth pages themselves
  if (path === "/auth" || path.startsWith("/auth/") || path.startsWith("/auth?")) {
    return;
  }
  try {
    const entry: RedirectEntry = {
      path,
      expiresAt: Date.now() + EXPIRY_MS,
    };
    localStorage.setItem(REDIRECT_TARGET_KEY, JSON.stringify(entry));
  } catch {
    // Ignore storage availability errors
  }
}

export function getAndClearRedirectTarget(): string | null {
  try {
    const raw = localStorage.getItem(REDIRECT_TARGET_KEY);
    if (!raw) return null;
    localStorage.removeItem(REDIRECT_TARGET_KEY);
    const entry: RedirectEntry = JSON.parse(raw);
    if (typeof entry?.expiresAt !== "number" || Date.now() > entry.expiresAt) {
      return null;
    }
    const path = entry.path;
    if (isValidRedirectPath(path)) {
      return path;
    }
  } catch {
    // Malformed JSON or storage access blocked
  }
  return null;
}
